import { MED_FREQUENCY, type DurationUnit, type MedFrequency } from '../lib/medication-frequency';

/**
 * Maps mode 8's free-text medication sig onto the dose dialog's typed fields.
 *
 * Mode 8 emits what was said — `frequency: "twice daily"`, `duration: "1 week"`. The dialog
 * wants `MedFrequency` + numbers. Without this, "take Adol for 1 week twice per day" cannot
 * pre-fill anything, which is the moment the feature is supposed to earn its keep.
 *
 * ── The rule that matters more than coverage ──────────────────────────────────────────────
 *
 * An unrecognised input returns UNDEFINED. Never a default, never a best guess.
 *
 * A wrong dose frequency is a prescribing error. An empty field the clinician has to fill is
 * mildly annoying; a plausible wrong one they skim past is dangerous. So the parser is
 * deliberately narrow — it recognises the common unambiguous phrasings and declines
 * everything else, including things it could *almost* work out.
 *
 * NOTE ON THE WIRE VALUES: these are `MED_FREQUENCY` (`everyday` / `needed` / `day_interval` /
 * `specific`), not the camelCase key names. PROD-1445 records what happens when those diverge —
 * the earlier `every_day`/`as_needed` spellings mis-parsed in the e-prescription path.
 */

export interface ParsedFrequency {
  frequency: MedFrequency;
  /** Doses per day, when the phrasing states one. */
  timesPerDay?: number;
  /** Gap in days, for `day_interval` frequencies. */
  dayInterval?: number;
}

export interface ParsedDuration {
  durationValue: number;
  durationUnit: DurationUnit;
}

export type ParsedSig = Partial<ParsedFrequency & ParsedDuration>;

const WORD_NUMBERS: Readonly<Record<string, number>> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  once: 1,
  twice: 2,
  thrice: 3,
};

/** Latin dosing abbreviations, as doses per day. */
const LATIN_PER_DAY: Readonly<Record<string, number>> = {
  od: 1,
  qd: 1,
  bd: 2,
  bid: 2,
  tds: 3,
  tid: 3,
  qds: 4,
  qid: 4,
};

const normalise = (raw: string): string =>
  raw.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();

const toNumber = (token: string): number | undefined => {
  if (/^\d+$/.test(token)) return Number(token);
  return WORD_NUMBERS[token];
};

/**
 * Parses a free-text frequency. Returns undefined when the text is absent, empty, or not one
 * of the recognised unambiguous forms.
 */
export function parseFrequency(raw: string | undefined): ParsedFrequency | undefined {
  if (!raw) return undefined;
  const text = normalise(raw);
  if (!text) return undefined;

  // As-needed — check first, since "as needed" contains no numeric signal to confuse later
  // branches.
  if (/\b(prn|as needed|when required|if required|as required)\b/.test(text)) {
    return { frequency: MED_FREQUENCY.asNeeded };
  }

  // Latin abbreviations, whole-token only so "bid" does not match inside another word.
  const latin = LATIN_PER_DAY[text];
  if (latin !== undefined) {
    return { frequency: MED_FREQUENCY.everyDay, timesPerDay: latin };
  }

  // Multi-day intervals → day_interval.
  if (/\bevery other day\b|\balternate days?\b/.test(text)) {
    return { frequency: MED_FREQUENCY.daysInterval, dayInterval: 2 };
  }
  const dayGap = text.match(/\bevery (\w+) days\b/);
  if (dayGap) {
    const n = toNumber(dayGap[1]);
    if (n !== undefined && n > 1) {
      return { frequency: MED_FREQUENCY.daysInterval, dayInterval: n };
    }
    return undefined;
  }

  // Hourly intervals — only convert when the interval divides a 24-hour day exactly.
  // "every 5 hours" is a real instruction but is NOT n-times-daily; rounding it would invent
  // a schedule the clinician did not say.
  const hourly = text.match(/\b(?:every |q ?)(\w+) ?(?:hours|hour|hrs|hr|h)\b/);
  if (hourly) {
    const hours = toNumber(hourly[1]);
    if (hours !== undefined && hours > 0 && 24 % hours === 0) {
      return { frequency: MED_FREQUENCY.everyDay, timesPerDay: 24 / hours };
    }
    return undefined;
  }

  // N times a day.
  const perDay = text.match(/\b(\w+) times? (?:a|per|each) day\b|\b(\w+) times? daily\b/);
  if (perDay) {
    const n = toNumber(perDay[1] ?? perDay[2]);
    if (n !== undefined) return { frequency: MED_FREQUENCY.everyDay, timesPerDay: n };
    return undefined;
  }

  // "twice daily" / "twice a day" — the count word carries the number directly.
  const countWord = text.match(/\b(once|twice|thrice)\b/);
  if (countWord && /\b(daily|a day|per day|each day|day)\b/.test(text)) {
    return { frequency: MED_FREQUENCY.everyDay, timesPerDay: WORD_NUMBERS[countWord[1]] };
  }

  // Plain daily, with no stated count.
  if (/^(daily|every ?day|everyday)$/.test(text) || /\b(once daily|once a day|every day)\b/.test(text)) {
    return { frequency: MED_FREQUENCY.everyDay };
  }

  return undefined;
}

/**
 * Parses a free-text duration. Returns undefined for open-ended instructions ("ongoing",
 * "until review") — those are real answers, but they are not a number and a unit.
 */
export function parseDuration(raw: string | undefined): ParsedDuration | undefined {
  if (!raw) return undefined;
  const text = normalise(raw);
  if (!text) return undefined;

  // UK shorthand: 5/7 = 5 days, 2/52 = 2 weeks, 3/12 = 3 months.
  const shorthand = text.match(/^(\d+)\s*\/\s*(7|52|12)$/);
  if (shorthand) {
    const value = Number(shorthand[1]);
    const unit: DurationUnit =
      shorthand[2] === '7' ? 'day' : shorthand[2] === '52' ? 'wk' : 'mo';
    return { durationValue: value, durationUnit: unit };
  }

  const match = text.match(/\b(\w+)\s*(days?|d|weeks?|wks?|w|months?|mos?|m)\b/);
  if (!match) return undefined;

  const value = toNumber(match[1]);
  if (value === undefined || value <= 0) return undefined;

  const unitToken = match[2];
  const durationUnit: DurationUnit = /^d/.test(unitToken)
    ? 'day'
    : /^w/.test(unitToken)
      ? 'wk'
      : 'mo';

  return { durationValue: value, durationUnit };
}

/**
 * Convenience wrapper for a mode-8 medication proposal. Each half is parsed independently, so
 * a recognisable frequency still pre-fills even when the duration is open-ended.
 */
export function parseMedicationSig(input: {
  frequency?: string;
  duration?: string;
}): ParsedSig {
  return { ...parseFrequency(input.frequency), ...parseDuration(input.duration) };
}
